import { useState, useRef, useMemo, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { QrCode, Upload, FileText, X, Sparkles, Zap, Shield, Clock } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import heroImage from "@/assets/hotel.png";
import { useTranslation } from "react-i18next";
import { supportedLanguages } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Progress } from "./ui/progress";
import { API_BASE_URL } from "@/config";
import { SiteFooter } from "./SiteFooter";

interface DirectoryUploadProps {
  onUpload: (args: {
    qrCodePngUrl?: string;
    qrCodeSvgUrl?: string;
    qrCodeUrl?: string;
  }) => void;
}

export function DirectoryUpload({ onUpload }: DirectoryUploadProps) {
  const { t, i18n } = useTranslation();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<"idle" | "requesting" | "uploading" | "done" | "error">("idle");
  const [progress, setProgress] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPrivacyBanner, setShowPrivacyBanner] = useState(false);

  const currentLanguage = useMemo(() => {
    const resolved = i18n.resolvedLanguage ?? i18n.language;
    if (!resolved) {
      return supportedLanguages[0].code;
    }

    const exactMatch = supportedLanguages.find((language) => language.code === resolved);
    if (exactMatch) {
      return exactMatch.code;
    }

    const partialMatch = supportedLanguages.find((language) => resolved.startsWith(language.code));
    return partialMatch ? partialMatch.code : supportedLanguages[0].code;
  }, [i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("conciergie_privacy_ack");
      setShowPrivacyBanner(stored !== "true");
    } catch {
      setShowPrivacyBanner(true);
    }
  }, []);

  const handleDismissPrivacy = () => {
    setShowPrivacyBanner(false);
    try {
      window.localStorage.setItem("conciergie_privacy_ack", "true");
    } catch {
      // ignore storage errors
    }
  };

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value);
  };

  const startUpload = async (file: File) => {
    try {
      setStatus("requesting");
      setProgress(0);
      setErrorMessage(null);

      const existingHotelId = window.localStorage.getItem("hotel_id");

      const body: Record<string, unknown> = {
        file_name: file.name,
      };
      if (existingHotelId) {
        body.hotel_id = existingHotelId;
      }

      const response = await fetch(`${API_BASE_URL}/public_info/hotels/generate-upload-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Upload API failed with status ${response.status}`);
      }

      const data = (await response.json()) as {
        hotel_id?: string;
        qr_code_id?: string;
        upload_url?: string;
        object_key?: string;
        qr_code_png_url?: string;
        qr_code_svg_url?: string;
        qr_code_target_url?: string;
      };

      if (data.hotel_id) {
        window.localStorage.setItem("hotel_id", data.hotel_id);
      }
      if (data.qr_code_id) {
        window.localStorage.setItem("qr_code_id", data.qr_code_id);
      }
      if (data.qr_code_target_url) {
        try {
          window.localStorage.setItem("qr_code_target_url", data.qr_code_target_url);
        } catch {
          // best-effort only
        }
      }

      if (!data.upload_url) {
        throw new Error("Upload URL not returned by API.");
      }

      const uploadUrl = data.upload_url;

      setStatus("uploading");
      setProgress(0);

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setProgress(percent);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setProgress(100);
            resolve();
          } else {
            reject(new Error(`S3 upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error("Network error while uploading to storage."));
        };

        xhr.open("PUT", uploadUrl, true);
        xhr.send(file);
      });

      setStatus("done");
      onUpload({
        qrCodePngUrl: data.qr_code_png_url,
        qrCodeSvgUrl: data.qr_code_svg_url,
        qrCodeUrl: data.qr_code_target_url,
      });
    } catch (err) {
      console.error(err);
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Unexpected error during upload.");
    }
  };

  const handleFileChange = (file: File | null) => {
    setUploadedFile(file);
    setStatus("idle");
    setProgress(0);
    setErrorMessage(null);

    if (file) {
      void startUpload(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileChange(e.target.files[0]);
    }
  };

  const handleRemove = () => {
    setUploadedFile(null);
    setStatus("idle");
    setProgress(0);
    setErrorMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!uploadedFile) {
      return;
    }

    await startUpload(uploadedFile);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <QrCode className="h-6 w-6 text-white" />
              </div>
              <span className="text-2xl">Conciergie</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="hidden text-sm text-gray-500 sm:inline">{t("nav.language")}</span>
                <Select value={currentLanguage} onValueChange={handleLanguageChange}>
                  <SelectTrigger size="sm" className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white shadow-lg border border-gray-200">
                    {supportedLanguages.map((language) => (
                      <SelectItem
                        key={language.code}
                        value={language.code}
                        hideIndicator
                        className="data-[state=checked]:bg-blue-50"
                      >
                        {language.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="ghost">{t("nav.signIn")}</Button>
              <Button>{t("nav.getStarted")}</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-white to-white">
        <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-4 py-2">
                <Sparkles className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-900">{t("directory.hero.badge")}</span>
              </div>
              
              <div className="space-y-6">
                <h1 className="text-5xl lg:text-6xl leading-tight">
                  {t("directory.hero.titlePart1")}{" "}
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {t("directory.hero.titleHighlight")}
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  {t("directory.hero.description")}
                </p>
              </div>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-green-600" />
                  </div>
                  <span className="text-sm">{t("directory.hero.bullet1")}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-green-600" />
                  </div>
                  <span className="text-sm">{t("directory.hero.bullet2")}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-green-600" />
                  </div>
                  <span className="text-sm">{t("directory.hero.bullet3")}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-purple-600/20 rounded-3xl blur-3xl" />
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-gray-900/10">
                <ImageWithFallback
                  src={heroImage}
                  alt="Modern luxury hotel lobby"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl mb-4">{t("directory.steps.heading")}</h2>
            <p className="text-lg text-gray-600">
              {t("directory.steps.subheading")}
            </p>
          </div>

          <Card className="border-2 shadow-xl">
            <CardContent className="pt-10 pb-10">
              <div className="space-y-8">
                {/* Step 1: Upload */}
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 text-white flex items-center justify-center shadow-lg">
                      <span className="font-semibold">1</span>
                    </div>
                    <h3 className="text-2xl">{t("directory.upload.title")}</h3>
                  </div>

                  {uploadedFile ? (
                    <div className="border-2 border-green-500 bg-green-50 rounded-xl p-6 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center">
                          <FileText className="h-7 w-7 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-lg">{uploadedFile.name}</p>
                          <p className="text-sm text-gray-600">
                            {t("directory.upload.fileSize", {
                              size: (uploadedFile.size / 1024).toFixed(2),
                            })}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleRemove}
                          className="hover:bg-red-50"
                        >
                          <X className="h-5 w-5 text-red-600" />
                        </Button>
                      </div>
                      {(status === "requesting" ||
                        status === "uploading" ||
                        status === "done" ||
                        status === "error") && (
                        <div className="mt-4 space-y-2">
                          {status === "requesting" && (
                            <p className="text-sm text-gray-600">
                              {t(
                                "directory.upload.requestingUploadUrl",
                                "Requesting secure upload link...",
                              )}
                            </p>
                          )}
                          {status === "uploading" && (
                            <>
                              <p className="text-sm text-gray-600">
                                {t("directory.upload.uploading", "Uploading your file...")}
                              </p>
                              <Progress value={progress} />
                              <p className="text-xs text-gray-500">{progress}%</p>
                            </>
                          )}
                          {status === "done" && (
                            <p className="text-sm text-green-700">
                              {t(
                                "directory.upload.completed",
                                "Upload complete. You're all set!",
                              )}
                            </p>
                          )}
                          {status === "error" && errorMessage && (
                            <p className="text-sm text-red-600">
                              {t("directory.upload.error", "Upload failed")}: {errorMessage}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                        dragActive 
                          ? "border-blue-500 bg-blue-50 shadow-lg" 
                          : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <Upload className="h-10 w-10 text-blue-600" />
                      </div>
                      <p className="text-xl mb-2">
                        {t("directory.upload.dropTitle")}
                      </p>
                      <p className="text-sm text-gray-500 mb-6">
                        {t("directory.upload.dropSubtitle")}
                      </p>
                      <Button
                        type="button"
                        size="lg"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {t("directory.upload.button")}
                      </Button>
                      <p className="text-xs text-gray-400 mt-4">
                        {t("directory.upload.formats")}
                      </p>
                      <p className="text-xs text-gray-500 mt-2 max-w-md mx-auto">
                        {t(
                          "directory.upload.consent",
                          "Your data is encrypted with AWS KMS.  By selecting a file, you consent to it being uploaded to Conciergie and accept full responsibility for ensuring it does not contains personal nor sensitive data. Please only upload files with information that can be made publically available.",
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Step 2: Download */}
                <div className={uploadedFile ? "opacity-100" : "opacity-40"}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center">
                      <span className="font-semibold">2</span>
                    </div>
                    <h3 className="text-2xl">{t("directory.download.title")}</h3>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6">
                    <p className="text-gray-700">
                      {t("directory.download.info")}
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    size="lg"
                    className="w-full h-14 text-lg shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    onClick={handleSubmit}
                    disabled={!uploadedFile || status === "requesting" || status === "uploading"}
                  >
                    {status === "uploading"
                      ? t("directory.actions.uploading", "Uploading...")
                      : t("directory.actions.submit")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Features Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl mb-4">{t("directory.features.title")}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t("directory.features.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-8 pb-8">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl mb-3">{t("directory.features.cards.instant.title")}</h3>
                <p className="text-gray-600">
                  {t("directory.features.cards.instant.description")}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-8 pb-8">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-xl mb-3">{t("directory.features.cards.secure.title")}</h3>
                <p className="text-gray-600">
                  {t("directory.features.cards.secure.description")}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-8 pb-8">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-xl mb-3">{t("directory.features.cards.access.title")}</h3>
                <p className="text-gray-600">
                  {t("directory.features.cards.access.description")}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <SiteFooter />

      {/* Minimal, dismissible privacy banner */}
      {showPrivacyBanner && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 backdrop-blur-sm">
          <div className="mx-auto flex max-w-4xl flex-col gap-3 px-4 py-3 text-xs text-gray-600 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <p className="leading-relaxed">
              We use privacy-friendly analytics to understand how Conciergie is used and to improve the service. 
              If you upload documents or share contact details, we process them only to provide the service, respond to you, 
              and send product updates where requested. See our{" "}
              <a
                href="/privacy-policy"
                className="underline underline-offset-2 hover:text-gray-800"
              >
                Privacy Policy
              </a>{" "}
              for details about how we protect your data and your rights.
            </p>
            <div className="flex shrink-0 justify-end">
              <button
                type="button"
                onClick={handleDismissPrivacy}
                className="rounded-full bg-gray-900 px-4 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-gray-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}